<script setup lang="ts">
import { computed, ref, watchEffect } from "vue"
import { useI18n } from "vue-i18n"
import { getTranslatedLabel, formatDateTime } from "@shared/helpers/utils"
import { sanitizeHtml } from "@shared/helpers/sanitizeHtml"
import { useHelp } from "@/pages/admin/help/composables/useHelp"
import type { HelpView } from "@/pages/admin/help/types"
import "quill/dist/quill.snow.css"

const props = defineProps<{
  uniqueId: string
  showTitle?: boolean
}>()

const emit = defineEmits<{
  (e: "loaded", help: HelpView): void
  (e: "not-found"): void
}>()

const { t } = useI18n()
const { getView, trackView, submitFeedback } = useHelp()

const help = ref<HelpView | null>(null)

const title = computed(() => (help.value ? getTranslatedLabel(help.value.title) : ""))
const description = computed(() => sanitizeHtml(help.value ? getTranslatedLabel(help.value.description) : ""))
const lastUpdated = computed(() => (help.value ? formatDateTime(help.value.lastUpdated) : ""))

watchEffect(async () => {
  if (!props.uniqueId) {
    help.value = null
    return
  }
  const result = await getView(props.uniqueId)
  help.value = result
  if (!result) {
    emit("not-found")
    return
  }
  emit("loaded", result)
  trackView(props.uniqueId)
})

const showLinkModal = ref(false)
const pendingUrl = ref("")

function onContentClick(event: MouseEvent) {
  const anchor = (event.target as HTMLElement | null)?.closest("a")
  if (!anchor) return

  let url: URL
  try {
    url = new URL(anchor.href)
  } catch {
    return
  }
  // Only intercept external http(s) links; let internal navigation behave normally.
  if (url.protocol !== "http:" && url.protocol !== "https:") return
  if (url.origin === window.location.origin) return

  event.preventDefault()
  pendingUrl.value = anchor.href
  showLinkModal.value = true
}

function confirmOpenLink() {
  if (pendingUrl.value) window.open(pendingUrl.value, "_blank", "noopener,noreferrer")
  showLinkModal.value = false
  pendingUrl.value = ""
}

async function onFeedback(isHelpful: boolean) {
  if (!help.value) return
  const toggled = help.value.myFeedback === isHelpful ? null : isHelpful
  const ok = await submitFeedback(props.uniqueId, toggled)
  if (ok) help.value.myFeedback = toggled
}
</script>

<template>
  <div>
    <h1 v-if="props.showTitle && title" class="va-h5 mb-4">{{ title }}</h1>

    <div class="ql-snow">
      <!-- eslint-disable-next-line vue/no-v-html -- description is sanitized via sanitizeHtml() -->
      <div class="ql-editor ql-display" @click="onContentClick" v-html="description"></div>
    </div>

    <div
      v-if="help?.isTrackingEnabled"
      class="feedback-box mt-8 flex flex-row items-center justify-center gap-4 rounded-lg px-6 py-3"
    >
      <span class="text-sm font-medium">{{ t("help.wasThisHelpful") }}</span>
      <VaButton
        preset="secondary"
        icon="mso-thumb_up"
        size="small"
        :color="help.myFeedback === true ? 'primary' : 'secondary'"
        :aria-label="t('help.helpful')"
        @click="onFeedback(true)"
      >
        {{ t("help.helpful") }}
      </VaButton>
      <VaButton
        preset="secondary"
        icon="mso-thumb_down"
        size="small"
        :color="help.myFeedback === false ? 'danger' : 'secondary'"
        :aria-label="t('help.notHelpful')"
        @click="onFeedback(false)"
      >
        {{ t("help.notHelpful") }}
      </VaButton>
    </div>

    <p v-if="help" class="text-sm text-secondary mt-6">{{ t("help.lastUpdated", { date: lastUpdated }) }}</p>

    <VaModal
      v-model="showLinkModal"
      size="small"
      max-width="440px"
      :title="t('general.warning')"
      :ok-text="t('help.externalLinkConfirm')"
      :cancel-text="t('general.cancel')"
      mobile-fullscreen
      close-button
      @ok="confirmOpenLink"
    >
      <p>{{ t("help.externalLinkMessage") }}</p>
      <p class="font-semibold break-all mt-2">{{ pendingUrl }}</p>
    </VaModal>
  </div>
</template>

<style scoped>
.ql-display :deep(iframe) {
  display: block;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  border: 0;
}

.ql-display :deep(img) {
  max-width: 100%;
  height: auto;
}

.ql-display :deep(a) {
  color: var(--va-primary);
}

.ql-display :deep(blockquote) {
  border-left-color: var(--va-background-border);
}

.ql-display {
  counter-reset: list-0 list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
}

.ql-display :deep(ol) {
  padding-left: 1.5em;
}

.ql-display :deep(li[data-list="bullet"])::before {
  content: "\2022";
  display: inline-block;
  white-space: nowrap;
  width: 1.2em;
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
}

.ql-display :deep(li[data-list="ordered"]) {
  counter-increment: list-0;
}
.ql-display :deep(li[data-list="ordered"])::before {
  content: counter(list-0, decimal) ". ";
  display: inline-block;
  white-space: nowrap;
  min-width: 1.2em;
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
}

.ql-display :deep(li[data-list="ordered"].ql-indent-1) {
  counter-increment: list-1;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-1)::before {
  content: counter(list-1, lower-alpha) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-2) {
  counter-increment: list-2;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-2)::before {
  content: counter(list-2, lower-roman) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-3) {
  counter-increment: list-3;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-3)::before {
  content: counter(list-3, decimal) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-4) {
  counter-increment: list-4;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-4)::before {
  content: counter(list-4, lower-alpha) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-5) {
  counter-increment: list-5;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-5)::before {
  content: counter(list-5, lower-roman) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-6) {
  counter-increment: list-6;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-6)::before {
  content: counter(list-6, decimal) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-7) {
  counter-increment: list-7;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-7)::before {
  content: counter(list-7, lower-alpha) ". ";
}
.ql-display :deep(li[data-list="ordered"].ql-indent-8) {
  counter-increment: list-8;
}
.ql-display :deep(li[data-list="ordered"].ql-indent-8)::before {
  content: counter(list-8, lower-roman) ". ";
}

.ql-display :deep(.ql-indent-1:not(.ql-direction-rtl)) {
  padding-left: 3em;
}
.ql-display :deep(li.ql-indent-1:not(.ql-direction-rtl)) {
  padding-left: 4.5em;
}
.ql-display :deep(.ql-indent-2:not(.ql-direction-rtl)) {
  padding-left: 6em;
}
.ql-display :deep(li.ql-indent-2:not(.ql-direction-rtl)) {
  padding-left: 7.5em;
}
.ql-display :deep(.ql-indent-3:not(.ql-direction-rtl)) {
  padding-left: 9em;
}
.ql-display :deep(li.ql-indent-3:not(.ql-direction-rtl)) {
  padding-left: 10.5em;
}
.ql-display :deep(.ql-indent-4:not(.ql-direction-rtl)) {
  padding-left: 12em;
}
.ql-display :deep(li.ql-indent-4:not(.ql-direction-rtl)) {
  padding-left: 13.5em;
}
.ql-display :deep(.ql-indent-5:not(.ql-direction-rtl)) {
  padding-left: 15em;
}
.ql-display :deep(li.ql-indent-5:not(.ql-direction-rtl)) {
  padding-left: 16.5em;
}
.ql-display :deep(.ql-indent-6:not(.ql-direction-rtl)) {
  padding-left: 18em;
}
.ql-display :deep(li.ql-indent-6:not(.ql-direction-rtl)) {
  padding-left: 19.5em;
}
.ql-display :deep(.ql-indent-7:not(.ql-direction-rtl)) {
  padding-left: 21em;
}
.ql-display :deep(li.ql-indent-7:not(.ql-direction-rtl)) {
  padding-left: 22.5em;
}
.ql-display :deep(.ql-indent-8:not(.ql-direction-rtl)) {
  padding-left: 24em;
}
.ql-display :deep(li.ql-indent-8:not(.ql-direction-rtl)) {
  padding-left: 25.5em;
}

.feedback-box {
  background: var(--va-background-element);
  border: 1px solid var(--va-background-border);
}
</style>

<style>
.ql-display .ql-align-center {
  text-align: center;
}
.ql-display .ql-align-right {
  text-align: right;
}
.ql-display .ql-align-justify {
  text-align: justify;
}

.ql-display .ql-indent-1:not(.ql-direction-rtl) {
  padding-left: 3em;
}
.ql-display li.ql-indent-1:not(.ql-direction-rtl) {
  padding-left: 4.5em;
}
.ql-display .ql-indent-2:not(.ql-direction-rtl) {
  padding-left: 6em;
}
.ql-display li.ql-indent-2:not(.ql-direction-rtl) {
  padding-left: 7.5em;
}
.ql-display .ql-indent-3:not(.ql-direction-rtl) {
  padding-left: 9em;
}
.ql-display li.ql-indent-3:not(.ql-direction-rtl) {
  padding-left: 10.5em;
}
.ql-display .ql-indent-4:not(.ql-direction-rtl) {
  padding-left: 12em;
}
.ql-display li.ql-indent-4:not(.ql-direction-rtl) {
  padding-left: 13.5em;
}
.ql-display .ql-indent-5:not(.ql-direction-rtl) {
  padding-left: 15em;
}
.ql-display li.ql-indent-5:not(.ql-direction-rtl) {
  padding-left: 16.5em;
}
.ql-display .ql-indent-6:not(.ql-direction-rtl) {
  padding-left: 18em;
}
.ql-display li.ql-indent-6:not(.ql-direction-rtl) {
  padding-left: 19.5em;
}
.ql-display .ql-indent-7:not(.ql-direction-rtl) {
  padding-left: 21em;
}
.ql-display li.ql-indent-7:not(.ql-direction-rtl) {
  padding-left: 22.5em;
}
.ql-display .ql-indent-8:not(.ql-direction-rtl) {
  padding-left: 24em;
}
.ql-display li.ql-indent-8:not(.ql-direction-rtl) {
  padding-left: 25.5em;
}
</style>
